import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminStocks from './AdminStocks';
import './Admin.css';

const QUOTE_MODES = [
    { id: '1d', label: '1 day', hint: 'Last two calendar days (captures the latest session).' },
    { id: '5d', label: '5 days', hint: 'Five calendar days ending today.' },
    { id: '1m', label: '1 month', hint: 'About one calendar month ending today.' },
    { id: 'range', label: 'Date range', hint: 'Use the start and end fields below (YYYY-MM-DD).' },
];

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const HISTORY_START_DATE = '2000-01-01';

/** Normalize pasted dates (trim, Unicode minus → ASCII hyphen). */
function normalizeAdminDateInput(raw) {
    return String(raw).trim().replace(/\u2212/g, '-');
}

/** Today as YYYY-MM-DD in UTC (matches API bulk refresh end dates). */
function todayISODateUTC() {
    return new Date().toISOString().slice(0, 10);
}

/** True if ymd is a real calendar date (not e.g. 2024-02-30). */
function isValidISODateString(ymd) {
    if (!ISO_DATE_RE.test(ymd)) return false;
    const [y, m, d] = ymd.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

const Admin = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [pwUserId, setPwUserId] = useState(null);
    const [pwValue, setPwValue] = useState('');
    const [pwSaving, setPwSaving] = useState(false);
    const [pwError, setPwError] = useState('');
    const [pwSuccess, setPwSuccess] = useState('');

    const [quoteMode, setQuoteMode] = useState('1d');
    const [quoteStart, setQuoteStart] = useState('');
    const [quoteEnd, setQuoteEnd] = useState('');
    const [quoteRefreshing, setQuoteRefreshing] = useState(false);
    const [quoteError, setQuoteError] = useState('');
    const [quoteResult, setQuoteResult] = useState(null);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const r = await fetch('/api/admin/users', { credentials: 'include' });
            if (!r.ok) throw new Error('Failed to load users');
            const data = await r.json();
            setUsers(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const updateUser = async (id, updates) => {
        setError('');
        try {
            const r = await fetch(`/api/admin/users/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(updates),
            });
            if (!r.ok) {
                const data = await r.json();
                throw new Error(data.error || 'Update failed');
            }
            await fetchUsers();
        } catch (err) {
            setError(err.message);
        }
    };

    const openPasswordEditor = (id) => {
        setPwUserId(id);
        setPwValue('');
        setPwError('');
        setPwSuccess('');
    };

    const closePasswordEditor = () => {
        setPwUserId(null);
        setPwValue('');
        setPwError('');
    };

    const savePassword = async (u) => {
        if (pwValue.length < 8) {
            setPwError('Password must be at least 8 characters.');
            return;
        }
        setPwError('');
        setPwSaving(true);
        try {
            const r = await fetch(`/api/admin/users/${u.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ password: pwValue }),
            });
            if (!r.ok) {
                const data = await r.json();
                throw new Error(data.error || 'Password update failed');
            }
            closePasswordEditor();
            setPwSuccess(`Password updated for ${u.username}.`);
        } catch (err) {
            setPwError(err.message);
        } finally {
            setPwSaving(false);
        }
    };

    const runBulkStockQuoteRefresh = async (body) => {
        setQuoteError('');
        setQuoteResult(null);
        setQuoteRefreshing(true);
        try {
            const r = await fetch('/api/admin/stocks/quotes/refresh-all', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(body),
            });
            const contentType = r.headers.get('content-type') || '';
            let data;
            if (contentType.includes('application/json')) {
                data = await r.json();
            } else {
                const text = (await r.text()).trim();
                if (r.status === 504) {
                    throw new Error(
                        'Gateway timed out waiting for the bulk refresh. '
                        + 'The job may still be running on the server — check API logs. '
                        + 'Try a shorter date range or redeploy with a longer nginx proxy timeout.',
                    );
                }
                throw new Error(text.slice(0, 240) || `Refresh failed (HTTP ${r.status})`);
            }
            if (!r.ok) throw new Error(data.error || 'Refresh failed');
            setQuoteResult(data);
        } catch (err) {
            setQuoteError(err.message);
        } finally {
            setQuoteRefreshing(false);
        }
    };

    const refreshAllStockQuotes = async () => {
        const body = { mode: quoteMode };
        if (quoteMode === 'range') {
            const start = normalizeAdminDateInput(quoteStart);
            const end = normalizeAdminDateInput(quoteEnd);
            if (!start || !end) {
                setQuoteError('Choose a start and end date for custom range.');
                return;
            }
            if (!isValidISODateString(start) || !isValidISODateString(end)) {
                setQuoteError(
                    'Use calendar dates as YYYY-MM-DD with leading zeros (e.g. 2024-06-01).',
                );
                return;
            }
            body.start = start;
            body.end = end;
        }
        await runBulkStockQuoteRefresh(body);
    };

    const refreshAllStockQuotesSince2000 = async () => {
        const end = todayISODateUTC();
        const confirmed = window.confirm(
            `Download daily bars for every watchlist symbol from ${HISTORY_START_DATE} through ${end}? `
            + 'This runs one Yahoo fetch per symbol and can take many minutes.',
        );
        if (!confirmed) return;
        await runBulkStockQuoteRefresh({
            mode: 'range',
            start: HISTORY_START_DATE,
            end,
        });
    };

    if (loading) return <div className="admin"><p>Loading users…</p></div>;

    return (
        <div className="admin">
            <div className="admin-header-row">
                <h1>User Management</h1>
                <button
                    type="button"
                    className="admin-blog-link-btn"
                    onClick={() => navigate('/admin/blog')}
                >
                    Manage blog
                </button>
            </div>
            {error && <div className="alert-error">{error}</div>}

            <section className="admin-stock-refresh" aria-labelledby="admin-stock-refresh-heading">
                <h2 id="admin-stock-refresh-heading">Stock prices (database)</h2>
                <p className="admin-stock-refresh-intro">
                    Fetch the latest Yahoo Finance daily bars for <strong>every symbol on a user&apos;s
                    watchlist</strong> and upsert into <code>markets</code>.
                </p>
                <fieldset className="admin-stock-refresh-modes">
                    <legend className="admin-stock-refresh-legend">Date window</legend>
                    {QUOTE_MODES.map(m => (
                        <label key={m.id} className="admin-stock-refresh-radio">
                            <input
                                type="radio"
                                name="quote-refresh-mode"
                                value={m.id}
                                checked={quoteMode === m.id}
                                onChange={() => setQuoteMode(m.id)}
                                disabled={quoteRefreshing}
                            />
                            <span className="admin-stock-refresh-radio-label">{m.label}</span>
                            <span className="admin-stock-refresh-radio-hint">{m.hint}</span>
                        </label>
                    ))}
                </fieldset>
                {quoteMode === 'range' && (
                    <div className="admin-stock-refresh-range">
                        <label className="admin-stock-refresh-date">
                            Start
                            <input
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                spellCheck={false}
                                placeholder="YYYY-MM-DD"
                                aria-describedby="admin-stock-date-format-hint"
                                value={quoteStart}
                                onChange={e => setQuoteStart(e.target.value)}
                                disabled={quoteRefreshing}
                            />
                        </label>
                        <label className="admin-stock-refresh-date">
                            End
                            <input
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                spellCheck={false}
                                placeholder="YYYY-MM-DD"
                                aria-describedby="admin-stock-date-format-hint"
                                value={quoteEnd}
                                onChange={e => setQuoteEnd(e.target.value)}
                                disabled={quoteRefreshing}
                            />
                        </label>
                    </div>
                )}
                {quoteMode === 'range' && (
                    <p id="admin-stock-date-format-hint" className="admin-stock-refresh-date-hint">
                        Format: <kbd>YYYY-MM-DD</kbd> (for example <kbd>2024-06-01</kbd>).
                    </p>
                )}
                {quoteError && <div className="alert-error admin-stock-refresh-msg">{quoteError}</div>}
                <div className="admin-stock-refresh-actions">
                    <button
                        type="button"
                        className="admin-stock-refresh-btn"
                        onClick={refreshAllStockQuotes}
                        disabled={quoteRefreshing}
                    >
                        {quoteRefreshing ? 'Updating…' : 'Update all stock prices'}
                    </button>
                    <button
                        type="button"
                        className="admin-stock-refresh-btn admin-stock-refresh-btn-secondary"
                        onClick={refreshAllStockQuotesSince2000}
                        disabled={quoteRefreshing}
                    >
                        {quoteRefreshing ? 'Updating…' : `Download all since ${HISTORY_START_DATE}`}
                    </button>
                </div>
                {quoteResult && (
                    <div className="admin-stock-refresh-result" role="status">
                        <p>
                            <strong>Window:</strong> {quoteResult.start} — {quoteResult.end}{' '}
                            <span className="admin-stock-refresh-meta">(mode: {quoteResult.mode})</span>
                        </p>
                        <p>
                            <strong>Symbols:</strong> {quoteResult.symbols_ok} of {quoteResult.symbols_total}{' '}
                            updated · <strong>Rows upserted:</strong> {quoteResult.rows_upserted}
                        </p>
                        {quoteResult.errors && quoteResult.errors.length > 0 && (
                            <div className="admin-stock-refresh-errors">
                                <strong>Errors ({quoteResult.errors.length})</strong>
                                <ul>
                                    {quoteResult.errors.map((e, i) => (
                                        <li key={`${e.symbol}-${i}`}>
                                            <code>{e.symbol}</code>: {e.error}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </section>

            <AdminStocks reloadKey={quoteResult} />

            {pwSuccess &&<div className="alert-success" role="status">{pwSuccess}</div>}
            <div className="users-table-wrapper">
                <table className="users-table">
                    <thead>
                        <tr>
                            <th>Username</th>
                            <th>Email</th>
                            <th>Registered</th>
                            <th>Status</th>
                            <th>Role</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(u => (
                            <React.Fragment key={u.id}>
                            <tr className={!u.is_approved ? 'pending' : ''}>
                                <td>{u.username}</td>
                                <td>{u.email}</td>
                                <td>{new Date(u.created_at).toLocaleDateString()}</td>
                                <td>
                                    <span className={`status-badge ${u.is_approved ? 'approved' : 'pending'}`}>
                                        {u.is_approved ? 'Approved' : 'Pending'}
                                    </span>
                                </td>
                                <td>
                                    <span className={`status-badge ${u.is_admin ? 'admin' : 'user'}`}>
                                        {u.is_admin ? 'Admin' : 'User'}
                                    </span>
                                </td>
                                <td>
                                    <div className="actions">
                                        {!u.is_approved ? (
                                            <button
                                                className="action-btn approve"
                                                onClick={() => updateUser(u.id, { is_approved: true })}
                                            >
                                                Approve
                                            </button>
                                        ) : (
                                            <button
                                                className="action-btn deny"
                                                onClick={() => updateUser(u.id, { is_approved: false })}
                                            >
                                                Revoke
                                            </button>
                                        )}
                                        {u.id !== user.user_id && (
                                            <button
                                                className={`action-btn ${u.is_admin ? 'demote' : 'promote'}`}
                                                onClick={() => updateUser(u.id, { is_admin: !u.is_admin })}
                                            >
                                                {u.is_admin ? 'Remove Admin' : 'Make Admin'}
                                            </button>
                                        )}
                                        <button
                                            className="action-btn set-password"
                                            onClick={() => (
                                                pwUserId === u.id ? closePasswordEditor() : openPasswordEditor(u.id)
                                            )}
                                        >
                                            Set Password
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            {pwUserId === u.id && (
                                <tr className="password-editor-row">
                                    <td colSpan="6">
                                        <form
                                            className="password-editor"
                                            onSubmit={e => { e.preventDefault(); savePassword(u); }}
                                        >
                                            <label className="password-editor-label">
                                                New password for <strong>{u.username}</strong>
                                                <input
                                                    type="password"
                                                    autoComplete="new-password"
                                                    autoFocus
                                                    minLength={8}
                                                    value={pwValue}
                                                    onChange={e => setPwValue(e.target.value)}
                                                    disabled={pwSaving}
                                                />
                                            </label>
                                            <button type="submit" className="action-btn approve" disabled={pwSaving}>
                                                {pwSaving ? 'Saving…' : 'Save'}
                                            </button>
                                            <button
                                                type="button"
                                                className="action-btn cancel"
                                                onClick={closePasswordEditor}
                                                disabled={pwSaving}
                                            >
                                                Cancel
                                            </button>
                                            {pwError && <span className="password-editor-error">{pwError}</span>}
                                        </form>
                                    </td>
                                </tr>
                            )}
                            </React.Fragment>
                        ))}
                        {users.length === 0 && (
                            <tr>
                                <td colSpan="6" className="no-users">No users found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Admin;
